import $ from "jquery";
import tampuuriWindowObject from "./tampuuriWindowObject.js";

const favouriteTables = () => {
  const { constructApartmentName } = tampuuriWindowObject();
  const costUnitTableBody = "#cost-unit-table tbody";
  const areaTableBody = "#area-table tbody";
  const costUnitContainer = "#cost-unit-container";
  const areaContainer = "#area-container";
  const freeApartmentButtonSelector =
    ".free-apartments .hoas-card--item .favourite, .free-apartments .hoas-card--item .favourite-heart";
  const allFavouriteButtonSelector = ".favourite, .favourite-heart";
  const removeButtonSelector = ".remove-single";
  const { hoasFavourites, themeData } = /** @type {any} */ (window);

  /**
   * Sets up everything related to the favourites tables (renders + listeners).
   * @returns {void}
   */
  const initialize = () => {
    renderAllTables();
    bindRemoveRowListeners();
    bindFreeApartmentButtons();
  };

  /**
   * Renders both the cost unit/apartment table and the area table.
   */
  const renderAllTables = () => {
    generateApartmentsAndCostUnits();
    generateAreas();
  };

  /**
   * Ensures clicks on the free-apartment cards keep the tables in sync.
   * @returns {void}
   */
  const bindFreeApartmentButtons = () => {
    const buttons = document.querySelectorAll(freeApartmentButtonSelector);
    if (!buttons?.length) {
      return;
    }

    buttons.forEach((button) => {
      if (!button) {
        return;
      }

      button.addEventListener("click", () => {
        generateApartmentsAndCostUnits();
        bindRemoveRowListeners();
      });
    });
  };

  /**
   * Attaches the remove-row handler to every delete button in the tables.
   * @returns {void}
   */
  const bindRemoveRowListeners = () => {
    const buttons = document.querySelectorAll(removeButtonSelector);
    if (!buttons?.length) {
      return;
    }

    buttons.forEach((button) => {
      button.addEventListener("click", handleRemoveRow);
    });
  };

  /**
   * Shared click handler for each remove button; deletes the data and rerenders as needed.
   * @param {Event} event
   * @returns {void}
   */
  const handleRemoveRow = (event) => {
    const target = event.currentTarget;
    if (!target) {
      return;
    }

    const payload = hoasFavourites.getParsedObject(
      /** @type {HTMLElement} */
      (target).getAttribute("data-favourite-object"),
    );

    if (!payload) {
      return;
    }

    hoasFavourites.removeDataByKey(payload?.type, payload);

    const isArea = payload?.type === hoasFavourites.AREA_KEY;
    if (isArea) {
      generateAreas();
    } else {
      generateApartmentsAndCostUnits();
    }

    bindRemoveRowListeners();
    syncFreeApartmentButtons(payload);
  };

  /**
   * Toggles the free-apartment hearts/buttons to reflect the removal.
   * @param {any} removedObject
   * @returns {void}
   */
  const syncFreeApartmentButtons = (removedObject) => {
    const { id: removedId } = removedObject ?? {};

    const buttons = document.querySelectorAll(allFavouriteButtonSelector);
    if (!buttons?.length) {
      return;
    }

    buttons.forEach((button) => {
      const object = hoasFavourites.getParsedObject(
        button.getAttribute("data-favourite-object"),
      );

      if (!object || object?.id !== removedId) {
        return;
      }

      const element = $(button);
      element.removeClass("active");
      if (element.hasClass("favourite-heart")) {
        return;
      }

      element.find(".modified-text").text(themeData?.addToFavourite);
    });
  };

  /**
   * Returns a textual surface area for a row, regardless of whether it is a cost unit or apartment.
   * @param {any} row
   * @returns {string}
   */
  const generateSurfaceArea = (row) => {
    const { surface_areas, surface_area } = row ?? {};

    if (surface_areas) {
      const { costunit } = surface_areas;
      return `${costunit?.min_surface_area} - ${costunit?.max_surface_area} m2`;
    }

    if (surface_area) {
      return `${surface_area} m2`;
    }

    return "";
  };

  /**
   * Returns a rent string for a row (cost-unit range or apartment rent).
   * @param {any} row
   * @returns {string}
   */
  const generateRent = (row) => {
    const { rent, rents } = row ?? {};

    if (rents) {
      const { costunit } = rents;
      return `${costunit?.min_rent} - ${costunit?.max_rent} €`;
    }
    if (rent) {
      return `${rent} €`;
    }

    return "";
  };

  /**
   * Builds the markup for cost unit/apartment table rows.
   * @param {any} rows
   * @returns {string}
   */
  const renderCostUnitRows = (rows) => {
    return rows
      .map((/** @type {any} */ row) => {
        const { address, location, apartment_type } = row ?? {};

        const addressCell = address
          ? `<td class="first"><span class="address">${constructApartmentName(address)}</span></td>`
          : "";
        const locationCell = location
          ? `<td class="second"><span class="location">${location}</span></td>`
          : "";

        return `
        <tr>
          ${addressCell}
          ${locationCell}
          <td class="third">
            <span class="apartment-type">${apartment_type ?? ""}</span>
            <span class="surface-area">${generateSurfaceArea(row)}</span>
            <span class="rent">${generateRent(row)}</span>
          </td>
          <td role="presentation" class="fourth">
            <button
              aria-label="${themeData?.removeFavourite}"
              data-favourite-object='${JSON.stringify(row)}'
              class="remove-button remove-single p-2">
              <span aria-hidden="true">${themeData?.trashCan}</span>
            </button>
          </td>
        </tr>`;
      })
      .join("");
  };

  /**
   * Rebuilds the cost-unit/apartment table based on hoasFavourites.
   */
  const generateApartmentsAndCostUnits = () => {
    const costUnits = hoasFavourites.getCostUnits();
    const apartments = hoasFavourites.getApartments();

    const rows = [];
    if (Array.isArray(costUnits) && costUnits.length > 0) {
      rows.push(...costUnits);
    }
    if (Array.isArray(apartments) && apartments.length > 0) {
      rows.push(...apartments);
    }

    if (!rows.length) {
      $(costUnitTableBody).empty();
      $(costUnitContainer).css({ display: "none" });
      return;
    }

    $(costUnitTableBody).html(renderCostUnitRows(rows));
    if ($(costUnitContainer).css("display") === "none") {
      $(costUnitContainer).css({ display: "block" });
    }
  };

  /**
   * Converts price level keywords to repeated icon strings.
   * @param {string} level
   * @returns {string}
   */
  const generatePriceLevel = (level) => {
    const levelMap = {
      edullinen: 1,
      kohtuullinen: 2,
      kallis: 3,
    };
    const indicator = /** @type {any} */ (levelMap)[level] ?? 0;
    return themeData?.priceLevel?.repeat(indicator) ?? "";
  };

  /**
   * Builds a display name for an area row (name + city).
   * @param {any} row
   * @returns {string}
   */
  const renderAreaName = (row) => {
    const { name, city } = row ?? {};

    if (name && city) {
      return `${name}, ${city}`;
    }
    if (name) {
      return name;
    }
    if (city) {
      return city;
    }
    return "";
  };

  /**
   * Builds the markup for the area table rows.
   * @param {any} areas
   * @returns {string}
   */
  const renderAreaRows = (areas) => {
    return areas
      .map((/** @type {any} */ row) => {
        const { link, price_level } = row ?? {};

        return `
      <tr>
        <td class="first">
          <span class="address">${renderAreaName(row)}</span>
        </td>
        <td class="second">
          <span class="priceLevel">${themeData["Hintataso"]}</span>
          ${generatePriceLevel(price_level)}
        </td>
        <td class="third">
          <a class="open-area" href="${link}">
            ${themeData?.openAreaPage}
            <div class="desktop d-none d-sm-flex">${themeData?.arrowForward}</div>
            <div class="mobile d-flex d-sm-none">${themeData?.hoasEye}</div>
          </a>
        </td>
        <td role="presentation" class="fourth">
          <button
            aria-label="${themeData?.removeFavourite}"
            data-favourite-object='${JSON.stringify(row)}'
            class="remove-button remove-single p-2">
            <span aria-hidden="true">${themeData?.trashCan}</span>
          </button>
        </td>
      </tr>`;
      })
      .join("");
  };
  /**
   * Rebuilds the area table from hoasFavourites.
   * @returns {void}
   */
  const generateAreas = () => {
    const areas = hoasFavourites.getAreas();
    if (!Array.isArray(areas) || !areas.length) {
      $(areaTableBody).empty();
      $(areaContainer).css({ display: "none" });
      return;
    }

    $(areaTableBody).html(renderAreaRows(areas));
    if ($(areaContainer).css("display") === "none") {
      $(areaContainer).css({ display: "block" });
    }
  };

  return { initialize };
};

$(function () {
  const { initialize } = favouriteTables();
  initialize();
});
